import React, { useState, useEffect } from 'react';
import {
    LinearProgress,
    Button,
    IconButton,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Autocomplete,
    TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
// import { assignOperator, removeOperator } from '../api/operatorsApi';

import { fetchOperators,addSubOperator, removeSubOperator, fetchOperator } from "../../features/operator/operatorSlice";
import {useAppDispatch} from "../../store";
import {useAlert} from "react-alert";

const ManagesOperators = ({ labels = {}, resourceId }) => {
    const alert = useAlert();
    // States
    const [operators, setOperators] = useState([]);
    const [managedOperators, setManagedOperators] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [availableOperators, setAvailableOperators] = useState({});
    const [selectedOperator, setSelectedOperator] = useState(null); // Changed to null for Autocomplete
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [operatorToDelete, setOperatorToDelete] = useState(null);
    const dispatch = useAppDispatch();

    // Default labels
    const defaultLabels = {
        managesOperators: 'Manages Operators',
        addOperator: 'Add Operator',
        selectOperator: 'Select Operator',
        edit: 'Edit',
        cancel: 'Cancel',
        save: 'Save',
        deleteConfirmTitle: 'Remove Operator',
        deleteConfirmMessage: 'Are you sure you want to remove this operator?',
        delete: 'Delete',
        noOperators: 'No operators assigned',
        organization: 'Organization',
        name: 'Name',
        email: 'Email',
        actions: 'Actions',
        ...labels,
    };

    // Fetch operators on component mount
    useEffect(() => {
        loadOperators();
        loadManagedOperators();
    }, [resourceId]);

    // Load managed operators from the current operator's manages field
    const loadManagedOperators = async () => {
        try {
            await dispatch(fetchOperator({
                operatorId: resourceId,
                successCallback: (operator) => {
                    if (operator.manages && Array.isArray(operator.manages)) {
                        setManagedOperators(operator.manages);
                    } else {
                        setManagedOperators([]);
                    }
                },
                errorCallback: (error) => {
                    console.error('Failed to fetch managed operators:', error);
                    setManagedOperators([]);
                }
            }));
        } catch (error) {
            console.error('Failed to load managed operators:', error);
            setManagedOperators([]);
        }
    };

    // Load operators from API
    const loadOperators = async () => {
        setIsLoading(true);

        try {
            const result = await dispatch(fetchOperators(resourceId));
            const response = result.payload.data;
            if (response.allOperators) {
                // Transform the operators data into a more detailed format
                const operatorMap = response.allOperators.reduce((acc, op) => {
                    acc[op._id] = {
                        name: op['operator/name'],
                        email: op['operator/email'],
                        organization: op['operator/organization'] || 'N/A',
                        displayName: `${op['operator/organization'] || 'N/A'} | ${op['operator/name']} - ${op['operator/email']}`
                    };
                    return acc;
                }, {});
                setAvailableOperators(operatorMap);
            }
            setIsLoading(false);
        } catch (error) {
            console.error('Failed to fetch operators:', error);
        } finally {
            setIsLoading(false);
        }
    };

    // Toggle edit mode
    const handleEditClick = () => {
        if (!isEditMode) {
            loadOperators();
        }
        setIsEditMode(!isEditMode);
        setSelectedOperator(null); // Reset to null for Autocomplete
    };

    // Handle operator selection for Autocomplete
    const handleOperatorChange = (event, newValue) => {
        setSelectedOperator(newValue);
    };

    // Add selected operator
    const handleAddOperator = async () => {
        if (!selectedOperator) return;

        try {
            await dispatch(addSubOperator({
                subOperatorId: selectedOperator.id,
                managerOperatorId: resourceId,
                successCallback: () => {
                    alert.show("Operator added successfully", { type: "success" });
                    loadOperators(); // Reload operators list
                    loadManagedOperators(); // Reload managed operators list
                    setSelectedOperator(null); // Reset to null for Autocomplete
                }
            }));
        } catch (error) {
            alert.show("Failed to add operator", { type: "error" });
            console.error('Failed to assign operator:', error);
        }
    };

    // Open delete confirmation dialog
    const handleDeleteClick = (operator) => {
        setOperatorToDelete(operator);
        setDeleteDialogOpen(true);
    };

    // Close delete confirmation dialog
    const handleCloseDeleteDialog = () => {
        setDeleteDialogOpen(false);
        setOperatorToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!operatorToDelete) return;

        try {
            await dispatch(removeSubOperator({
                managerOperatorId: resourceId,
                subOperatorId: operatorToDelete.id,
                successCallback: () => {
                    alert.show("Operator removed successfully", {
                        type: "success"
                    });
                    loadOperators(); // Reload operators list
                    loadManagedOperators(); // Reload managed operators list
                }
            }));
        } catch (error) {
            alert.show("Failed to remove operator", {
                type: "error"
            });
            console.error('Failed to remove operator:', error);
        } finally {
            handleCloseDeleteDialog();
        }
    };

    // Prepare table data from managedOperators and sort by organization then name
    const tableData = managedOperators.map(operatorId => {
        const operatorInfo = availableOperators[operatorId];
        return {
            id: operatorId,
            organization: operatorInfo?.organization || 'N/A',
            name: operatorInfo?.name || 'Unknown',
            email: operatorInfo?.email || 'Unknown'
        };
    }).sort((a, b) => {
        // Sort by organization first, then by name
        const orgCompare = a.organization.localeCompare(b.organization);
        return orgCompare !== 0 ? orgCompare : a.name.localeCompare(b.name);
    });

    // Prepare options for Autocomplete
    const autocompleteOptions = Object.entries(availableOperators)
        .filter(([operatorId]) => !managedOperators.includes(operatorId))
        .map(([operatorId, operatorInfo]) => ({
            id: operatorId,
            label: operatorInfo.displayName,
            organization: operatorInfo.organization,
            name: operatorInfo.name,
            email: operatorInfo.email
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return (
        <Container>
            <TitleRow>
                <Headline>{defaultLabels.managesOperators}</Headline>
                <TextButton onClick={handleEditClick}>
                    {isEditMode ? defaultLabels.cancel : defaultLabels.edit}
                </TextButton>
            </TitleRow>
            <Divider />

            {isLoading ? (
                <LinearProgress sx={{ marginTop: 3 }} color="secondary" />
            ) : (
                <>
                    {isEditMode && (
                        <OperatorAssignmentRow>
                            <Autocomplete
                                sx={{ width: '70%', mr: 2 }}
                                size="small"
                                options={autocompleteOptions}
                                value={selectedOperator}
                                onChange={handleOperatorChange}
                                getOptionLabel={(option) => option.label || ''}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                filterOptions={(options, { inputValue }) => {
                                    if (!inputValue) return options;

                                    return options.filter(option =>
                                        option.organization.toLowerCase().includes(inputValue.toLowerCase()) ||
                                        option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
                                        option.email.toLowerCase().includes(inputValue.toLowerCase())
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={defaultLabels.selectOperator}
                                        color="secondary"
                                    />
                                )}
                                renderOption={(props, option, { inputValue }) => {
                                    const highlightText = (text, highlight) => {
                                        if (!highlight) return text;

                                        const parts = text.split(new RegExp(`(${highlight})`, 'gi'));
                                        return parts.map((part, index) =>
                                            part.toLowerCase() === highlight.toLowerCase() ?
                                                <span key={index} style={{ backgroundColor: '#fff3cd', fontWeight: 'bold' }}>{part}</span> :
                                                part
                                        );
                                    };

                                    return (
                                        <li {...props} key={option.id}>
                                            <Box sx={{ width: '100%' }}>
                                                <Typography variant="body2" component="div" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                                                    {highlightText(option.organization, inputValue)}
                                                </Typography>
                                                <Typography variant="body2" component="div" sx={{ color: 'text.secondary', fontSize: '0.875rem', lineHeight: 1.2, mt: 0.5 }}>
                                                    {highlightText(option.name, inputValue)} - {highlightText(option.email, inputValue)}
                                                </Typography>
                                            </Box>
                                        </li>
                                    );
                                }}
                                noOptionsText="No operators found"
                                clearOnEscape
                                selectOnFocus
                                handleHomeEndKeys
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                disabled={!selectedOperator}
                                onClick={handleAddOperator}
                            >
                                {defaultLabels.addOperator}
                            </Button>
                        </OperatorAssignmentRow>
                    )}

                    {tableData.length > 0 ? (
                        <StyledTableContainer component={Paper}>
                            <Table sx={{ minWidth: 650 }} aria-label="operators table">
                                <StyledTableHead>
                                    <TableRow>
                                        <StyledTableCell>{defaultLabels.organization}</StyledTableCell>
                                        <StyledTableCell>{defaultLabels.name}</StyledTableCell>
                                        <StyledTableCell>{defaultLabels.email}</StyledTableCell>
                                        {isEditMode && (
                                            <StyledTableCell align="center">{defaultLabels.actions}</StyledTableCell>
                                        )}
                                    </TableRow>
                                </StyledTableHead>
                                <TableBody>
                                    {tableData.map((operator) => (
                                        <StyledTableRow
                                            key={operator.id}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {operator.organization}
                                            </TableCell>
                                            <TableCell>{operator.name}</TableCell>
                                            <TableCell>{operator.email}</TableCell>
                                            {isEditMode && (
                                                <TableCell align="center">
                                                    <IconButton
                                                        className="delete-button"
                                                        aria-label="delete"
                                                        onClick={() => handleDeleteClick({ id: operator.id })}
                                                        color="error"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            )}
                                        </StyledTableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </StyledTableContainer>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                            {defaultLabels.noOperators}
                        </Box>
                    )}
                </>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {defaultLabels.deleteConfirmTitle}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {defaultLabels.deleteConfirmMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeleteDialog} color="primary">
                        {defaultLabels.cancel}
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" autoFocus>
                        {defaultLabels.delete}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default ManagesOperators;


// Styled components
const Container = styled('div')({
    padding: '20px',
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
    marginBottom: '20px',
    marginTop: '20px', // Add consistent spacing from headline

});

const TitleRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
});

const Headline = styled(Typography)({
    fontSize: '18px',
    fontWeight: 600,
    color: '#2E1A46',
});

const OperatorAssignmentRow = styled('div')({
    display: 'flex',
    alignItems: 'center',
    marginTop: '16px',
    marginBottom: '16px',
});

const TextButton = styled(Button)({
    textTransform: 'none',
    minWidth: 'auto',
    padding: '4px 8px',
    color: '#6B3FA0',
    fontWeight: 500,
    '&:hover': {
        backgroundColor: 'rgba(107, 63, 160, 0.04)',
    },
});

const StyledTableContainer = styled(TableContainer)({
    marginTop: '16px',
    boxShadow: 'none',
    border: '1px solid #f0f0f0',
});

const StyledTableHead = styled(TableHead)({
    backgroundColor: '#f8f9fa',
});

const StyledTableCell = styled(TableCell)({
    fontWeight: 600,
    color: '#2E1A46',
});

const StyledTableRow = styled(TableRow)({
    '&:hover .delete-button': {
        backgroundColor: '#ffebee',
    },
    '&:has(.delete-button:hover)': {
        backgroundColor: '#ffebee',
    },
    // Fallback for browsers that don't support :has()
    '&.row-hover': {
        backgroundColor: '#ffebee',
    },
});